import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SIGNATURE_MAX_LENGTH,
  SIGNATURE_LABEL_MAX_LENGTH,
  DEFAULT_SIGNATURE_LABEL,
  findSignatureById,
} from "../../utils/signatureUtils";
import { toEditorHtml, formatBytes } from "../../utils/mailUtils";

const SIGNATURE_IMAGE_MAX_BYTES = 256 * 1024;
const SIGNATURE_IMAGE_SIZES = {
  small: { label: "Small", width: 120 },
  medium: { label: "Medium", width: 200 },
  large: { label: "Large", width: 320 },
};
const DEFAULT_SIGNATURE_IMAGE_SIZE = "medium";

function SignatureSettings({
  signatureList,
  defaultSignatureId,
  loading,
  message,
  messageType,
  onSaveSignature,
  onDeleteSignature,
  onSetDefault,
  onCreateSignature,
  editable = true,
}) {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const newLabelInputRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const [selectionId, setSelectionId] = useState(defaultSignatureId || (signatureList[0]?.id ?? null));
  const [labelDraft, setLabelDraft] = useState("");
  const [signatureDraft, setSignatureDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [defaultingId, setDefaultingId] = useState(null);
  const [newSignatureForm, setNewSignatureForm] = useState({ label: "", content: "", makeDefault: false });
  const [imageBusy, setImageBusy] = useState(false);
  const [localNotice, setLocalNotice] = useState(null);
  const [hasSelectedImage, setHasSelectedImage] = useState(false);
  const [selectedImageSize, setSelectedImageSize] = useState(null);

  const showLocalNotice = useCallback((type, text) => {
    if (!text) return;
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
    }
    setLocalNotice({ type, text });
    noticeTimerRef.current = setTimeout(() => setLocalNotice(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) {
        clearTimeout(noticeTimerRef.current);
      }
    };
  }, []);

  const getSelectedImage = useCallback(() => {
    if (typeof window === "undefined") return null;
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0) return null;
    const focusNode = selection.focusNode;
    if (!focusNode) return null;
    const elementNode = focusNode.nodeType === 1 ? focusNode : focusNode.parentElement;
    const root = editorRef.current;
    if (!elementNode || !root) return null;
    const image = elementNode.tagName === "IMG" ? elementNode : elementNode.closest?.("img");
    if (!image || !root.contains(image)) return null;
    return image;
  }, []);

  const applySizeToImageNode = useCallback((image, sizeKey = DEFAULT_SIGNATURE_IMAGE_SIZE) => {
    if (!image) return;
    const key = SIGNATURE_IMAGE_SIZES[sizeKey] ? sizeKey : DEFAULT_SIGNATURE_IMAGE_SIZE;
    const preset = SIGNATURE_IMAGE_SIZES[key];
    if (!preset) return;
    image.style.maxWidth = "100%";
    image.style.height = "auto";
    image.style.width = `${preset.width}px`;
    image.dataset.signatureSize = key;
  }, []);

  const syncSelectedImage = useCallback(() => {
    const image = getSelectedImage();
    if (image) {
      const key = image.dataset.signatureSize || "custom";
      setSelectedImageSize(key);
      setHasSelectedImage(true);
    } else {
      setSelectedImageSize(null);
      setHasSelectedImage(false);
    }
  }, [getSelectedImage]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handler = () => syncSelectedImage();
    document.addEventListener("selectionchange", handler);
    return () => {
      document.removeEventListener("selectionchange", handler);
    };
  }, [syncSelectedImage]);

  const currentRecord = useMemo(() => findSignatureById(signatureList, selectionId), [signatureList, selectionId]);
  const isDefaultSelected = useMemo(() => String(selectionId) === String(defaultSignatureId), [selectionId, defaultSignatureId]);
  const signatureEditorDisabled = loading || saving || !currentRecord || !editable;

  useEffect(() => {
    if (!currentRecord) {
      setLabelDraft("");
      setSignatureDraft("");
      return;
    }
    setLabelDraft(currentRecord.label || "");
    setSignatureDraft(currentRecord.content || "");
  }, [currentRecord?.content, currentRecord?.label]);

  useEffect(() => {
    setSelectionId((prev) => {
      if (prev !== null && findSignatureById(signatureList, prev)) {
        return prev;
      }
      if (defaultSignatureId !== null && findSignatureById(signatureList, defaultSignatureId)) {
        return defaultSignatureId;
      }
      return signatureList[0]?.id ?? null;
    });
  }, [defaultSignatureId, signatureList]);

  const handleAddNewClick = useCallback(() => {
    setSelectionId(null);
    setTimeout(() => {
      try {
        newLabelInputRef.current?.focus();
        newLabelInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {}
    }, 50);
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    const nextHtml = toEditorHtml(signatureDraft || "");
    if (editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, [signatureDraft]);

  const focusEditor = useCallback(() => {
    const node = editorRef.current;
    if (node) {
      node.focus();
    }
  }, []);

  const executeFormattingAction = useCallback((command, value = null, afterFn = null) => {
    focusEditor();
    try {
      document.execCommand(command, false, value);
    } catch {}
    if (typeof afterFn === "function") {
      afterFn();
    }
  }, [focusEditor]);

  const handleEditorInput = useCallback(() => {
    const node = editorRef.current;
    if (!node) return;
    const html = node.innerHTML;
    if (html.length > SIGNATURE_MAX_LENGTH) {
      node.innerHTML = toEditorHtml(signatureDraft || "");
      showLocalNotice("error", "Signature size limit reached.");
      return;
    }
    setSignatureDraft(html);
  }, [signatureDraft, showLocalNotice]);

  const handleLabelChange = useCallback((event) => {
    const nextValue = (event.target.value || "").slice(0, SIGNATURE_LABEL_MAX_LENGTH);
    setLabelDraft(nextValue);
  }, []);

  const applySelectedImageSize = useCallback((sizeKey) => {
    const image = getSelectedImage();
    if (!image) {
      showLocalNotice("info", "Select an image in your signature first.");
      return;
    }
    applySizeToImageNode(image, sizeKey);
    handleEditorInput();
    setSelectedImageSize(sizeKey);
    setHasSelectedImage(true);
    const label = SIGNATURE_IMAGE_SIZES[sizeKey]?.label || "Updated";
    showLocalNotice("success", `Image resized to ${label.toLowerCase()}.`);
  }, [applySizeToImageNode, getSelectedImage, handleEditorInput, showLocalNotice]);

  const handleRemoveImage = useCallback(() => {
    const image = getSelectedImage();
    if (!image) {
      showLocalNotice("info", "Select an image to remove.");
      return;
    }
    image.remove();
    handleEditorInput();
    setSelectedImageSize(null);
    setHasSelectedImage(false);
    showLocalNotice("success", "Image removed from signature.");
  }, [getSelectedImage, handleEditorInput, showLocalNotice]);

  const handleImageSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.toLowerCase().startsWith("image/")) {
      showLocalNotice("error", "Please choose an image file.");
      return;
    }

    if (file.size > SIGNATURE_IMAGE_MAX_BYTES) {
      showLocalNotice(
        "error",
        `Images must be smaller than ${formatBytes(SIGNATURE_IMAGE_MAX_BYTES)}.`
      );
      return;
    }

    const reader = new FileReader();
    setImageBusy(true);

    reader.onload = () => {
      setImageBusy(false);
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) {
        showLocalNotice("error", "Unable to read the image.");
        return;
      }

      const node = editorRef.current;
      if (!node) return;

      const nextLength = node.innerHTML.length + dataUrl.length;
      if (nextLength > SIGNATURE_MAX_LENGTH) {
        showLocalNotice("error", "Adding this image would exceed the signature size limit.");
        return;
      }

      executeFormattingAction("insertImage", dataUrl, () => {
        const editorNode = editorRef.current;
        if (editorNode) {
          const images = editorNode.querySelectorAll("img");
          const latestImage = images[images.length - 1];
          if (latestImage) {
            applySizeToImageNode(latestImage, DEFAULT_SIGNATURE_IMAGE_SIZE);
          }
        }
        handleEditorInput();
        syncSelectedImage();
        showLocalNotice("success", "Image added to signature.");
      });
    };

    reader.onerror = () => {
      setImageBusy(false);
      showLocalNotice("error", "Unable to read the image file.");
    };

    reader.readAsDataURL(file);
  }, [applySizeToImageNode, executeFormattingAction, handleEditorInput, showLocalNotice, syncSelectedImage]);

  const handleEditorPaste = useCallback((event) => {
    if (signatureEditorDisabled) return;
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find((item) => item.kind === "file" && item.type.toLowerCase().startsWith("image/"));
    if (!imageItem) return;

    const file = imageItem.getAsFile();
    if (!file) return;

    event.preventDefault();

    if (file.size > SIGNATURE_IMAGE_MAX_BYTES) {
      showLocalNotice(
        "error",
        `Images must be smaller than ${formatBytes(SIGNATURE_IMAGE_MAX_BYTES)}.`
      );
      return;
    }

    const reader = new FileReader();
    setImageBusy(true);

    reader.onload = () => {
      setImageBusy(false);
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) {
        showLocalNotice("error", "Unable to read the pasted image.");
        return;
      }

      const node = editorRef.current;
      if (!node) return;

      const nextLength = node.innerHTML.length + dataUrl.length;
      if (nextLength > SIGNATURE_MAX_LENGTH) {
        showLocalNotice("error", "Pasted image would exceed the signature size limit.");
        return;
      }

      executeFormattingAction("insertImage", dataUrl, () => {
        const editorNode = editorRef.current;
        if (editorNode) {
          const images = editorNode.querySelectorAll("img");
          const latestImage = images[images.length - 1];
          if (latestImage) {
            applySizeToImageNode(latestImage, DEFAULT_SIGNATURE_IMAGE_SIZE);
          }
        }
        handleEditorInput();
        syncSelectedImage();
        showLocalNotice("success", "Image pasted into signature.");
      });
    };

    reader.onerror = () => {
      setImageBusy(false);
      showLocalNotice("error", "Unable to process the pasted image.");
    };

    reader.readAsDataURL(file);
  }, [applySizeToImageNode, executeFormattingAction, handleEditorInput, showLocalNotice, signatureEditorDisabled, syncSelectedImage]);

  const handleCopySignature = useCallback(async () => {
    const node = editorRef.current;
    if (!node) return;
    const html = node.innerHTML;
    if (!html) {
      showLocalNotice("info", "Nothing to copy yet.");
      return;
    }

    if (typeof navigator === "undefined") {
      showLocalNotice("error", "Clipboard is not available in this environment.");
      return;
    }

    try {
      if (navigator.clipboard?.write && typeof window !== "undefined" && window.ClipboardItem) {
        const htmlBlob = new Blob([html], { type: "text/html" });
        const textBlob = new Blob([node.innerText || ""], { type: "text/plain" });
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": htmlBlob,
            "text/plain": textBlob,
          }),
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(html);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = html;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showLocalNotice("success", "Signature copied to clipboard.");
    } catch {
      showLocalNotice("error", "Unable to copy the signature.");
    }
  }, [showLocalNotice]);

  const handleSave = useCallback(async (event) => {
    event.preventDefault();
    if (!currentRecord || typeof currentRecord.id !== "number") return;
    const payload = {
      label: (labelDraft || "").trim().slice(0, SIGNATURE_LABEL_MAX_LENGTH) || DEFAULT_SIGNATURE_LABEL,
      content: signatureDraft,
    };
    try {
      setSaving(true);
      await onSaveSignature?.(currentRecord.id, payload);
    } finally {
      setSaving(false);
    }
  }, [currentRecord, labelDraft, onSaveSignature, signatureDraft]);

  const handleDelete = useCallback(async () => {
    if (!currentRecord || typeof currentRecord.id !== "number") return;
    try {
      setDeletingId(currentRecord.id);
      await onDeleteSignature?.(currentRecord.id);
    } finally {
      setDeletingId(null);
    }
  }, [currentRecord, onDeleteSignature]);

  const handleSetDefault = useCallback(async () => {
    if (!currentRecord || typeof currentRecord.id !== "number") return;
    try {
      setDefaultingId(currentRecord.id);
      await onSetDefault?.(currentRecord.id);
    } finally {
      setDefaultingId(null);
    }
  }, [currentRecord, onSetDefault]);

  const handleNewSignatureChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setNewSignatureForm((prev) => {
      if (type === "checkbox") {
        return { ...prev, [name]: checked };
      }
      if (name === "label") {
        return { ...prev, label: (value || "").slice(0, SIGNATURE_LABEL_MAX_LENGTH) };
      }
      if (name === "content") {
        return { ...prev, content: (value || "").slice(0, SIGNATURE_MAX_LENGTH) };
      }
      return prev;
    });
  }, []);

  const handleCreate = useCallback(async (event) => {
    event.preventDefault();
    const label = (newSignatureForm.label || "").trim().slice(0, SIGNATURE_LABEL_MAX_LENGTH);
    const content = (newSignatureForm.content || "").trim();
    if (!content) return;
    const payload = {
      label: label || `Signature ${signatureList.length + 1}`,
      content: content.slice(0, SIGNATURE_MAX_LENGTH),
      makeDefault: Boolean(newSignatureForm.makeDefault),
    };
    try {
      setCreating(true);
      await onCreateSignature?.(payload);
      setNewSignatureForm({ label: "", content: "", makeDefault: false });
    } finally {
      setCreating(false);
    }
  }, [newSignatureForm, onCreateSignature, signatureList.length]);

  const signatureCharsRemaining = Math.max(0, SIGNATURE_MAX_LENGTH - (signatureDraft?.length || 0));
  const newSignatureCharsRemaining = Math.max(0, SIGNATURE_MAX_LENGTH - (newSignatureForm.content?.length || 0));

  return (
    <div className="mail-settings-card">
      <h2 className="mail-settings-title">Signature settings</h2>
      <p className="mail-settings-description">Manage the signatures that automatically append to your outgoing emails.</p>
      {loading ? <div className="mail-settings-status">Loading…</div> : null}
      <div className="mail-signatures-wrapper">
        <aside className="mail-signatures-list">
          <div className="mail-signatures-list-head">
            <span>Available signatures</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className="mail-signatures-count">{signatureList.length}</span>
              <button type="button" className="mail-button-primary" onClick={handleAddNewClick} disabled={loading || !editable}>
                Add signature
              </button>
            </div>
          </div>
          {signatureList.length ? (
            <ul>
              {signatureList.map((signature) => {
                const isSelected = String(signature.id) === String(selectionId);
                const isDefault = String(signature.id) === String(defaultSignatureId);
                return (
                  <li key={signature.id}>
                    <button
                      type="button"
                      className={`mail-signature-option ${isSelected ? "active" : ""}`}
                      onClick={() => setSelectionId(signature.id)}
                      disabled={loading}
                    >
                      <span className="mail-signature-option-label">{signature.label}</span>
                      {isDefault ? <span className="mail-pill">Default</span> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="mail-settings-status">No signatures available.</div>
          )}
        </aside>

        <div className="mail-signatures-editor">
          <form className="mail-settings-form" onSubmit={handleSave}>
            <label className="mail-settings-field">
              <span className="mail-settings-label">Label</span>
              <input
                value={labelDraft}
                onChange={handleLabelChange}
                placeholder="Signature label"
                disabled={signatureEditorDisabled}
              />
            </label>
            <label className="mail-settings-field">
              <span className="mail-settings-label">Mail signature</span>
              <div className="mail-compose-toolbar mail-signature-toolbar">
                <button
                  type="button"
                  title="Bold"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("bold", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-bold"></i>
                </button>
                <button
                  type="button"
                  title="Italic"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("italic", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-italic"></i>
                </button>
                <button
                  type="button"
                  title="Underline"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("underline", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-underline"></i>
                </button>
                <div className="mail-toolbar-divider" />
                <select
                  className="mail-toolbar-select"
                  onChange={(event) => executeFormattingAction("fontName", event.target.value, handleEditorInput)}
                  disabled={signatureEditorDisabled}
                >
                  <option value="">Font</option>
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times</option>
                  <option value="Courier New">Courier</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                </select>
                <select
                  className="mail-toolbar-select"
                  onChange={(event) => executeFormattingAction("fontSize", event.target.value, handleEditorInput)}
                  disabled={signatureEditorDisabled}
                >
                  <option value="">Size</option>
                  <option value="1">Small</option>
                  <option value="3">Normal</option>
                  <option value="5">Large</option>
                  <option value="7">Huge</option>
                </select>
                <div className="mail-toolbar-divider" />
                <button
                  type="button"
                  title="Align left"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("justifyLeft", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-align-left"></i>
                </button>
                <button
                  type="button"
                  title="Align center"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("justifyCenter", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-align-center"></i>
                </button>
                <button
                  type="button"
                  title="Align right"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("justifyRight", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-align-right"></i>
                </button>
                <div className="mail-toolbar-divider" />
                <label className="mail-color-btn" title="Text color">
                  <i className="fa-solid fa-palette"></i>
                  <input
                    type="color"
                    onChange={(event) => executeFormattingAction("foreColor", event.target.value, handleEditorInput)}
                    disabled={signatureEditorDisabled}
                  />
                </label>
                <button
                  type="button"
                  title="Insert image"
                  disabled={signatureEditorDisabled || imageBusy}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => imageInputRef.current?.click()}
                >
                  <i className="fa-solid fa-image"></i>
                </button>
                <div className="mail-toolbar-divider" />
                <button
                  type="button"
                  title="Small image"
                  disabled={signatureEditorDisabled || !hasSelectedImage}
                  className={selectedImageSize === "small" ? "is-active" : ""}
                  aria-pressed={selectedImageSize === "small"}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applySelectedImageSize("small")}
                >
                  S
                </button>
                <button
                  type="button"
                  title="Medium image"
                  disabled={signatureEditorDisabled || !hasSelectedImage}
                  className={selectedImageSize === "medium" ? "is-active" : ""}
                  aria-pressed={selectedImageSize === "medium"}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applySelectedImageSize("medium")}
                >
                  M
                </button>
                <button
                  type="button"
                  title="Large image"
                  disabled={signatureEditorDisabled || !hasSelectedImage}
                  className={selectedImageSize === "large" ? "is-active" : ""}
                  aria-pressed={selectedImageSize === "large"}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applySelectedImageSize("large")}
                >
                  L
                </button>
                <button
                  type="button"
                  title="Remove image"
                  disabled={signatureEditorDisabled || !hasSelectedImage}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleRemoveImage}
                >
                  <i className="fa-solid fa-trash-can"></i>
                </button>
                <button
                  type="button"
                  title="Bulleted list"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("insertUnorderedList", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-list-ul"></i>
                </button>
                <button
                  type="button"
                  title="Numbered list"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("insertOrderedList", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-list-ol"></i>
                </button>
                <div className="mail-toolbar-divider" />
                <button
                  type="button"
                  title="Insert link"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    const url = window.prompt("Enter link URL");
                    if (url) {
                      executeFormattingAction("createLink", url, handleEditorInput);
                    }
                  }}
                >
                  <i className="fa-solid fa-link"></i>
                </button>
                <button
                  type="button"
                  title="Remove formatting"
                  disabled={signatureEditorDisabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => executeFormattingAction("removeFormat", null, handleEditorInput)}
                >
                  <i className="fa-solid fa-eraser"></i>
                </button>
              </div>
              <div
                ref={editorRef}
                className={`mail-settings-editor ${signatureEditorDisabled ? "disabled" : ""}`}
                contentEditable={!signatureEditorDisabled}
                suppressContentEditableWarning
                data-placeholder="Type the closing lines you want to append to outgoing mail"
                onInput={handleEditorInput}
                onPaste={handleEditorPaste}
                aria-busy={imageBusy}
              />
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ display: "none" }}
                tabIndex={-1}
              />
            </label>
            <div className="mail-settings-meta">
              <span>{signatureCharsRemaining} characters remaining</span>
            </div>
            {localNotice ? (
              <div className={`mail-settings-message ${localNotice.type}`} aria-live="polite">
                {localNotice.text}
              </div>
            ) : null}
            {message && (
              <div className={`mail-settings-message ${messageType}`}>
                {message}
              </div>
            )}
            <div className="mail-settings-actions">
              <button type="submit" disabled={signatureEditorDisabled}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="mail-button-secondary"
                onClick={() => {
                  if (!currentRecord) return;
                  setLabelDraft(currentRecord.label || "");
                  setSignatureDraft(currentRecord.content || "");
                }}
                disabled={signatureEditorDisabled}
              >
                Reset
              </button>
            
              <button
                type="button"
                className="mail-button-secondary"
                onClick={handleSetDefault}
                disabled={
                  loading || saving || !currentRecord || isDefaultSelected || defaultingId === currentRecord?.id || !editable
                }
              >
                {defaultingId === currentRecord?.id ? "Updating…" : "Set default"}
              </button>
              <button
                type="button"
                className="mail-button-danger"
                onClick={handleDelete}
                disabled={
                  loading || saving || signatureList.length <= 1 || !currentRecord || deletingId === currentRecord?.id || !editable
                }
              >
                {deletingId === currentRecord?.id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </form>

          <form className="mail-settings-form secondary" onSubmit={handleCreate}>
            <h3 className="mail-settings-subtitle">Add signature</h3>
            <label className="mail-settings-field">
              <span className="mail-settings-label">Label</span>
              <input
                  name="label"
                  ref={newLabelInputRef}
                  value={newSignatureForm.label}
                  onChange={handleNewSignatureChange}
                  placeholder="Signature label"
                  disabled={loading || creating || !editable}
                />
            </label>
            <label className="mail-settings-field">
              <span className="mail-settings-label">Mail signature</span>
              <textarea
                name="content"
                className="mail-settings-textarea"
                value={newSignatureForm.content}
                onChange={handleNewSignatureChange}
                rows={4}
                placeholder="Enter the signature body"
                disabled={loading || creating || !editable}
              />
            </label>
            <div className="mail-settings-meta">
              <span>{newSignatureCharsRemaining} characters remaining</span>
            </div>
            <label className="mail-settings-checkbox">
              <input
                type="checkbox"
                name="makeDefault"
                checked={newSignatureForm.makeDefault}
                onChange={handleNewSignatureChange}
                disabled={loading || creating || !editable}
              />
              <span>Set as default after saving</span>
            </label>
            <div className="mail-settings-actions">
              <button type="submit" disabled={loading || creating || !editable}>
                {creating ? "Adding…" : "Add signature"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SignatureSettings;
